import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import {
  updateField,
  updateReviewData,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useDispatch, useSelector } from 'react-redux';
const ScrollableCalendar = ({ callback }: { callback: () => void }) => {
  // Get current date for initial state
  const stateData = useSelector((state: any) => state.flightSearch);
  const [isClicked, setIsClicked] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    // Initialize with stateData?.date if available, otherwise use server-side safe date
    const initialDate = stateData?.date ? new Date(stateData.date) : new Date();
    // Reset time to midnight to ensure consistent server/client rendering
    initialDate.setHours(0, 0, 0, 0);
    return initialDate;
  });
  const dispatch = useDispatch();

  // Function to generate dates for the calendar
  const generateDates = React.useCallback((startDate: Date) => {
    const dates = [];
    for (let i = 0; i < 8; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      // Reset time to midnight to ensure consistent rendering
      date.setHours(0, 0, 0, 0);
      dates.push({ date });
    }
    return dates;
  }, []);

  // Memoize dates to avoid unnecessary rerenders
  const [dates, setDates] = useState(() => generateDates(currentDate));

  // Handle navigation
  // const handlePrevWeek = () => {
  //   const newDate = new Date(currentDate);
  //   newDate.setDate(newDate.getDate() - 8);
  //   setCurrentDate(newDate);
  //   setDates(generateDates(newDate));
  // };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate days difference between current date and today
    const diffDays = Math.floor(
      (currentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    // If difference is more than 7 days, subtract 7 days
    // If less than 7 days, only subtract days until today
    const daysToSubtract = diffDays >= 7 ? 7 : diffDays;

    if (daysToSubtract > 0) {
      newDate.setDate(newDate.getDate() - daysToSubtract);
      setCurrentDate(newDate);
      setDates(generateDates(newDate));
    }
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 8);
    setCurrentDate(newDate);
    setDates(generateDates(newDate));
  };

  // Format date to display
  const formatDate = (date: Date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
    };
  };

  useEffect(() => {
    setCurrentDate(new Date(stateData?.date || new Date()));
    setDates(generateDates(new Date(stateData?.date || new Date())));
  }, [stateData?.date]);

  useEffect(() => {
    if (isClicked) {
      callback();
    }
  }, [isClicked, stateData?.date]);

  const onClickDate = async (date: Date) => {
    setCurrentDate(date);
    dispatch(updateField({ field: 'date', value: format(date, 'yyyy-MM-dd') }));
    dispatch(updateReviewData({ date: format(date, 'dd, MMM yyyy') }));
    setIsClicked(true);
  };

  return (
    <div className="mx-auto w-full rounded-2xl bg-white shadow-sm">
      <div className="relative flex items-center">
        {/* Left Navigation Arrow */}
        <button
          onClick={handlePrevWeek}
          disabled={new Date(currentDate).getDate() == new Date().getDate()}
          className={`absolute left-0 h-full w-12 rounded-l-3xl border-r bg-white pl-1 ${
            new Date(currentDate).getDate() == new Date().getDate()
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-gray-50'
          }`}
        >
          <ChevronLeft className="h-10 w-10 text-primary" />
        </button>

        {/* Calendar Container */}
        <div className="flex-1 overflow-hidden rounded-lg shadow">
          <div className={`flex justify-around px-10`}>
            {dates.map((item, index) => {
              const formattedDate = formatDate(item.date);
              return (
                <div
                  key={index}
                  onClick={() => onClickDate(item?.date)}
                  className={`flex-1 cursor-pointer border-x border-gray-200 p-3 text-center transition-colors`}
                >
                  <div className={`text-xs font-semibold text-black`}>
                    {formattedDate.day}, {formattedDate.month}{' '}
                    {formattedDate.date}
                  </div>
                  <div
                    className={`mt-2 text-xs font-medium ${
                      format(currentDate, 'dd, MMM yyyy') ===
                      format(item.date, 'dd, MMM yyyy')
                        ? 'text-green-600'
                        : 'text-black'
                    }`}
                  >
                    {format(item.date, 'dd, MMM yyyy')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Navigation Arrow */}
        {/* flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 transition-colors */}
        <button
          onClick={handleNextWeek}
          className={`absolute right-0 ${
            new Date(currentDate).getDate() >= new Date().getDate() + 30
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-gray-50'
          }`}
        >
          <ChevronRight className="h-10 w-10 text-primary" />
        </button>
      </div>
    </div>
  );
};

export default ScrollableCalendar;
